import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { GraduationCap, ArrowRight, ArrowLeft, Eye, EyeOff, Calendar, Sparkles, BookOpen, Phone, Clock, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const StudentAuthPage = () => {
  const navigate = useNavigate();
  const { login: authLogin, user } = useAuth();
  
  const [mode, setMode] = useState('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ageError, setAgeError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    mobile_number: '',
    date_of_birth: '',
    birth_time: '',
    birth_place: '',
  });

  useEffect(() => {
    if (user) {
      if (user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/instructions');
      }
    }
  }, [user, navigate]);

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateAge = (dob) => {
    if (!dob) return '';
    const age = calculateAge(dob);
    if (age < 15) {
      return 'You must be at least 15 years old for the Student Assessment.';
    }
    if (age > 25) {
      return 'The Student Assessment is designed for ages 15-25. Consider our Executive Assessment for professionals.';
    }
    return '';
  };

  const handleDobChange = (e) => {
    const dob = e.target.value;
    setFormData({ ...formData, date_of_birth: dob });
    setAgeError(validateAge(dob));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (mode === 'signup') {
      const ageValidation = validateAge(formData.date_of_birth);
      if (ageValidation) {
        setAgeError(ageValidation);
        toast.error(ageValidation);
        return;
      }
    }
    
    setLoading(true);
    
    try {
      if (mode === 'signin') {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
        authLogin(response.data.token, response.data.user);
        toast.success('Welcome back!');
        // Check if user has already paid - navigate accordingly
        const userData = response.data.user;
        if (userData.is_paid) {
          navigate('/instructions');
        } else {
          navigate('/payment');
        }
      } else {
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          mobile_number: formData.mobile_number,
          user_type: 'student',
          date_of_birth: formData.date_of_birth,
          birth_time: formData.birth_time,
          birth_place: formData.birth_place,
        });
        // Don't auto-login after registration - require user to sign in
        toast.success('Account created successfully! Please sign in to continue.');
        setMode('signin');
        // Clear password but keep email for convenience
        setFormData(prev => ({ ...prev, password: '' }));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4" data-testid="student-auth-page">
      {/* Background effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C5A065]/10 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#CC5500]/10 rounded-full filter blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back to home */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-400 hover:text-white"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-white">
            Lead<span className="text-[#CC5500]">Economics</span>™
          </h1>
          <p className="text-sm text-gray-500 mt-1">Career Discovery Intelligence</p>
        </div>

        {/* Student Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A065]/15 border border-[#C5A065]/30">
            <GraduationCap className="h-4 w-4 text-[#C5A065]" />
            <span className="text-sm font-medium text-[#C5A065]">
              Student Assessment
            </span>
          </div>
        </div>

        {/* Features */}
        <div className="flex justify-center gap-4 mb-6 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-[#C5A065]" />
            <span>55 Scenarios</span>
          </div>
          <div className="flex items-center gap-1">
            <BookOpen className="h-3 w-3 text-[#C5A065]" />
            <span>Career Insights</span>
          </div>
        </div>

        {/* Auth Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <Tabs value={mode} onValueChange={setMode} className="space-y-6">
            <TabsList className="w-full bg-white/5 border border-white/10 p-1 rounded-xl">
              <TabsTrigger 
                value="signin" 
                className="flex-1 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C5A065] data-[state=active]:to-[#CC5500] data-[state=active]:text-white rounded-lg"
                data-testid="signin-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="signup" 
                className="flex-1 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C5A065] data-[state=active]:to-[#CC5500] data-[state=active]:text-white rounded-lg"
                data-testid="signup-tab"
              >
                Create Account
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <>
                  <div>
                    <Label htmlFor="full_name" className="text-gray-400 text-sm">Full Name</Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Your full name"
                      required
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065]"
                      data-testid="fullname-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="mobile_number" className="text-gray-400 text-sm flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      Mobile Number
                    </Label>
                    <Input
                      id="mobile_number"
                      type="tel"
                      value={formData.mobile_number}
                      onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                      placeholder="+91 9876543210"
                      required
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065]"
                      data-testid="mobile-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="date_of_birth" className="text-gray-400 text-sm flex items-center gap-2">
                      <Calendar className="h-3 w-3" />
                      Date of Birth
                      <span className="text-[#C5A065] text-xs">(Ages 15-25)</span>
                    </Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={handleDobChange}
                      required
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065]"
                      data-testid="dob-input"
                    />
                    {ageError && (
                      <p className="text-red-400 text-xs mt-1" data-testid="age-error">{ageError}</p>
                    )}
                  </div>

                  {/* Birth Time - Required for accurate readings */}
                  <div>
                    <Label htmlFor="birth_time" className="text-gray-400 text-sm flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      Time of Birth <span className="text-[#C5A065] text-xs">(Required for accurate readings)</span>
                    </Label>
                    <Input
                      id="birth_time"
                      type="time"
                      value={formData.birth_time}
                      onChange={(e) => setFormData({ ...formData, birth_time: e.target.value })}
                      required
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065]"
                      data-testid="birth-time-input"
                    />
                    <p className="text-gray-500 text-xs mt-1">Check your birth certificate or ask family members</p>
                  </div>

                  {/* Birth Place - Required */}
                  <div>
                    <Label htmlFor="birth_place" className="text-gray-400 text-sm flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      Place of Birth <span className="text-[#C5A065] text-xs">(City, Country)</span>
                    </Label>
                    <Input
                      id="birth_place"
                      type="text"
                      value={formData.birth_place}
                      onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                      placeholder="e.g., Mumbai, India"
                      required
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065]"
                      data-testid="birth-place-input"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="text-gray-400 text-sm">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065]"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-400 text-sm">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C5A065] pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || (mode === 'signup' && ageError)}
                className="w-full bg-gradient-to-r from-[#C5A065] to-[#CC5500] hover:from-[#C5A065]/90 hover:to-[#CC5500]/90 text-white py-6 mt-4"
                data-testid="auth-submit-button"
              >
                {loading ? 'Processing...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </Tabs>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};
